<script setup>
import { storeToRefs } from 'pinia'
import { useMessageStore } from '~/stores/messages'
import SkeletonPlaceholder from '~/components/Base/SkeletonPlaceholder.vue'

// Fetch faq content
const { data: faqContent, isPlaceholderData: isFaqContentPlaceholderData, suspense: faqContentSuspense } = useFetchFaqs()

// Prefetch data on the server side
onServerPrefetch(async () => {
    await faqContentSuspense() // Wait for the query to finish fetching
})

// Fetch messages
const messagesStore = useMessageStore()
const { messages: staticKeys } = storeToRefs(messagesStore)

// Track user click
const trackFaqButtonClick = () => {
    trackUserClick({
        eventName: TRACKING_CONSTANTS.CLICK_EVENTS.FAQ_CONTACT_CLICK,
        clicked: TRACKING_CONSTANTS.MISC.FAQ_CONTACT_CLICK
    })
}

/**
 * Tracks a click on a question.
 *
 * @param {string} question - The text of the question.
 * @return {void}
 */
const trackQuestionClick = (question) => {
    trackUserClick({
        eventName: TRACKING_CONSTANTS.CLICK_EVENTS.QUESTION_CLICK,
        questionText: question
    })
}

// Import markdown
const { $md } = useNuxtApp()

const faqContactFormButtonConfig = computed(() => {
    return {
        buttonType: 'primary',
        type: 'internal',
        link: `mailto:${staticKeys.value?.contactform_to}?subject=${staticKeys.value?.contactform_subject}`,
        active: true,
        fn: () => trackFaqButtonClick()
    }
})

// Get loading class
const getFaqItemClass = computed(() => {
    return {
        'skeleton-placeholder-container': isFaqContentPlaceholderData.value
    }
})
</script>

<template>
    <header class="faq-page-header">
        <ContentMainHeadline :content-main-headline-config="{ title: staticKeys?.faq_headline_markdown }" />

        <p>{{ staticKeys?.faq_subline }}</p>
    </header>

    <ToggleItem
        v-for="({ question, answer }, index) in faqContent?.faq"
        :key="`toggle-item-${index}`"
        :class="getFaqItemClass"
        @click="trackQuestionClick(question)"
    >
        <template #title>
            <component
                :is="SkeletonPlaceholder"
                v-if="isFaqContentPlaceholderData"
                :skeleton-placeholder-config="{ type: 'headline', length: Math.floor(Math.random() * (90 - 70 + 1)) + 70 }"
            />

            <span v-else>{{ question }}</span>
        </template>

        <template #content>
            <div v-html="$md.render(answer || '')" />
        </template>
    </ToggleItem>

    <p v-if="!isFaqContentPlaceholderData" class="faq-btn">
        <BaseButton :base-button-config="faqContactFormButtonConfig">
            {{ staticKeys?.faq_contactform }}
        </BaseButton>
    </p>

    <DevOnly>
        <pre>{{ faqContent }}</pre>
    </DevOnly>
</template>

<style lang="scss">
.faq-page-header {
  text-align: center;

  @include bp(medium1) {
    text-align: left;
  }

  > p {
      margin-block-end: 20px;

    @include bp(medium1) {
        margin-block-end: 60px;
    }

    @include bp(large1) {
        margin-block-end: 40px;
    }
  }

  h1 {
        text-align: center;
        display: inline-block;

        @include bp(medium1) {
            text-align: left;
        }

        em {
            display: block;

            @include bp(medium1) {
                display: inline;
            }
        }
    }
}

.faq-btn {
  margin: 30px 0;

  @include bp(medium1) {
    text-align: center;
    margin: 70px 0 30px;
  }

  @include bp(large1) {
    margin: 100px 0 30px;
  }

  .btn-primary {
    width: 100%;

    @include bp(small2) {
      width: auto;
    }

    @include bp(large1) {
      width: 100%;
      max-width: 380px;
    }
  }
}
</style>
